'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';
import { useRole, UserRole } from '@/context/RoleContext';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import Link from 'next/link';

interface LoginFormData {
  email: string;
  password: string;
  role: UserRole;
  rememberMe: boolean;
}

interface LoginFormProps {
  onSwitchToRegister: () => void;
}

const roleOptions: { value: UserRole; label: string; initial: string; activeClass: string; hoverClass: string; bgClass: string; textClass: string }[] = [
  { value: 'applicant', label: 'Applicant', initial: 'AP', activeClass: 'border-orange-400 bg-orange-50', hoverClass: 'hover:border-orange-400', bgClass: 'bg-orange-100', textClass: 'text-orange-700' },
  { value: 'student', label: 'Student', initial: 'S', activeClass: 'border-teal-400 bg-teal-50', hoverClass: 'hover:border-teal-400', bgClass: 'bg-teal-100', textClass: 'text-teal-700' },
  { value: 'teacher', label: 'Teacher', initial: 'T', activeClass: 'border-blue-400 bg-blue-50', hoverClass: 'hover:border-blue-400', bgClass: 'bg-blue-100', textClass: 'text-blue-700' },
  { value: 'admin', label: 'Admin', initial: 'A', activeClass: 'border-purple-400 bg-purple-50', hoverClass: 'hover:border-purple-400', bgClass: 'bg-purple-100', textClass: 'text-purple-700' },
];

export default function LoginForm({ onSwitchToRegister }: LoginFormProps) {
  const router = useRouter();
  const { setRole } = useRole();
  const { signIn } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { register, handleSubmit, setValue, watch, formState: { errors }, setError } = useForm<LoginFormData>({
    defaultValues: { role: 'student', rememberMe: false }
  });

  const watchedRole = watch('role');

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      const result = await signIn(data.email, data.password);
      const supabase = createClient();
      const { data: profileData } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', result?.user?.id)
        .single();
      const userRole = (profileData?.role as UserRole) || (result?.user?.user_metadata?.role as UserRole) || data.role;
      setRole(userRole);
      toast.success(`Welcome back! Signing you in as ${userRole}...`);
      router.push('/dashboard');
      router.refresh();
    } catch (err: any) {
      const msg = err?.message || 'Invalid credentials';
      // Suppress refresh token errors — these are handled silently by AuthContext
      if (
        msg.includes('Refresh Token Not Found') ||
        msg.includes('Invalid Refresh Token') ||
        msg.includes('refresh_token_not_found') ||
        msg.includes('token_not_found')
      ) {
        return;
      }
      // Handle rate limit error gracefully
      if (
        msg.toLowerCase().includes('rate limit') ||
        msg.toLowerCase().includes('too many requests') ||
        msg.includes('429')
      ) {
        setError('email', { message: 'Too many login attempts. Please wait a moment and try again.' });
        return;
      }
      if (msg.toLowerCase().includes('invalid') || msg.toLowerCase().includes('credentials') || msg.toLowerCase().includes('password')) {
        setError('email', { message: 'Invalid email or password' });
      } else {
        setError('email', { message: msg });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-7">
        <h2 className="font-display font-700 text-gray-900 text-2xl mb-1">Welcome back</h2>
        <p className="text-gray-500 text-sm">Sign in to access your STARGA LMS portal</p>
      </div>

      {/* Role selector */}
      <div className="mb-5">
        <label className="label-base">I am a</label>
        <div className="grid grid-cols-4 gap-2">
          {roleOptions.map(r => (
            <label
              key={`role-${r.value}`}
              className={`
                flex flex-col items-center justify-center py-3 rounded-xl border-2 cursor-pointer transition-all duration-150
                ${watchedRole === r.value ? r.activeClass : `border-gray-200 ${r.hoverClass}`}
              `}
            >
              <input
                type="radio"
                value={r.value}
                {...register('role')}
                onChange={(e) => {
                  setValue('role', e.target.value as UserRole);
                }}
                className="sr-only"
              />
              <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-1.5 text-[10px] font-bold ${r.bgClass} ${r.textClass}`}>
                {r.initial}
              </div>
              <span className="text-[10px] font-medium text-gray-700 capitalize">{r.label}</span>
            </label>
          ))}
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="label-base">Email Address</label>
          <input
            type="email"
            placeholder="you@stargalms.edu"
            className={`input-base ${errors.email ? 'border-red-400 focus:border-red-400 focus:ring-red-500/20' : ''}`}
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email address' }
            })}
          />
          {errors.email && (
            <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
              <AlertCircle size={12} /> {errors.email.message}
            </p>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="label-base mb-0">Password</label>
            <button type="button" className="text-xs text-teal-600 hover:underline">Forgot password?</button>
          </div>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              className={`input-base pr-10 ${errors.password ? 'border-red-400 focus:border-red-400 focus:ring-red-500/20' : ''}`}
              {...register('password', { required: 'Password is required', minLength: { value: 6, message: 'Password must be at least 6 characters' } })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
              <AlertCircle size={12} /> {errors.password.message}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="rememberMe"
            {...register('rememberMe')}
            className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
          />
          <label htmlFor="rememberMe" className="text-sm text-gray-600 cursor-pointer">Remember me for 30 days</label>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-2.5 bg-teal-700 hover:bg-teal-800 active:scale-95 text-white font-display font-semibold text-sm rounded-xl transition-all duration-150 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed disabled:scale-100"
        >
          {isLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Signing in...
            </>
          ) : 'Sign In to Portal'}
        </button>
      </form>

      <p className="text-center text-sm text-gray-500 mt-5">
        New student?{' '}
        <button onClick={onSwitchToRegister} className="text-teal-600 font-semibold hover:underline">
          Register for a program
        </button>
      </p>

      <p className="text-center mt-3">
        <Link href="/admin-setup" className="text-xs text-gray-400 hover:text-gray-500 transition-colors">
          System Administrator Setup
        </Link>
      </p>
    </div>
  );
}